import { css } from '@emotion/react';
import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  Box,
  Button,
  Sheet,
  Stack,
  styled,
} from '@mui/joy';
import { Outlet, useParams, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import ClockIcon from '@/assets/icons/clock-countdown.svg?react';
import CrossIcon from '@/assets/icons/cross.svg?react';
import DuplicateIcon from '@/assets/icons/duplicate.svg?react';
import EditIcon from '@/assets/icons/edit.svg?react';
import ExpandArrowIcon from '@/assets/icons/expand-arrow.svg?react';
import PlusCircleIcon from '@/assets/icons/plus-circle.svg?react';
import { ErrorAlert } from '@/components/errors/ErrorAlert';
import { ButtonLink } from '@/components/router/ButtonLink';
import { IconButtonLink } from '@/components/router/IconButtonLink';
import { DuplicateModal } from '@/components/signs/modal/DuplicateModal';
import { EditSwitchModal } from '@/components/signs/modal/EditSwitchModal';
import { ProcessModal } from '@/components/signs/modal/ProcessModal';
import { SignCard } from '@/components/signs/modal/SignCard';
import { SignDetails } from '@/components/signs/modal/SignDetails';
import { SignGroupDetails } from '@/components/signs/modal/SignGroupDetails';
import { useSignSupportFlyTo } from '@/hooks/useSignSupportFlyTo';
import { useSignGroupQuery } from '@/queries/useSignGroupQuery';
import { useSignSupportQuery } from '@/queries/useSignSupportQuery';
import { theme as globalTheme } from '@/setup/theme';
import { useCreateSignStore } from '@/stores/useCreateSignStore';
import { CreateSignStep } from '@/types/CreateSignStep';

const StyledStack = styled(Stack)(
  ({ theme }) => css`
    display: inline-flex;
    align-items: flex-start;
    flex-direction: column;
    flex-shrink: 0;
    width: max(${theme.spacing(60)}, 30vw);
    max-width: ${theme.spacing(70)};
    height: 100%;
    overflow: auto;
    background-color: ${theme.palette.background.level1};
    border-left: 1px solid ${theme.palette.neutral.outlinedBorder};
    border-bottom: 1px solid ${theme.palette.neutral.outlinedBorder};
    padding: 0 ${theme.spacing(2)} ${theme.spacing(2)};
  `,
);

const StyledScrollBox = styled(Box)(
  ({ theme }) => css`
    background-color: ${theme.palette.common.white};
    flex: 1;
    overflow: auto;
    border-radius: 8px;
  `,
);

const StyledGroupAccordionGroup = styled(AccordionGroup)(
  () => css`
    width: 100%;
  `,
);

const StyledAccordionSummary = styled(AccordionSummary)(() => css``);

const StyledSignAccordionGroup = styled(AccordionGroup)(
  ({ theme }) => css`
    background-color: ${theme.palette.background.body};
    border-top: 1px solid ${theme.palette.neutral.outlinedBorder};
    width: 100%;
  `,
);

const StyledSignAccordion = styled(Accordion)(
  ({ theme }) => css`
    border-bottom: 1px solid ${theme.palette.neutral.outlinedBorder};
  `,
);

const StyledActionButtonStack = styled(Stack)(
  ({ theme }) => css`
    gap: 10px;
    padding: 16px;
    background-color: ${theme.palette.common.white};
    width: 100%;
    border-radius: 0 0 8px 8px;
  `,
);

export const signGroupDetailsSearchSchema = z.object({
  openProcess: z.preprocess((value) => {
    if (value === true || value === 'true') return true;
    if (value === false || value === 'false') return false;

    return undefined;
  }, z.boolean().optional()),
});

export function SignGroupDetailsRoute() {
  const { adminLevelId, signSupportId, signGroupId } = useParams({ strict: false });
  const search = useSearch({ strict: false });
  const { openProcess } = signGroupDetailsSearchSchema.parse(search);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDuplicateModal, setOpenDuplicateModal] = useState(false);
  const [openProcessModal, setOpenProcessModal] = useState(openProcess ?? false);
  const { setStep } = useCreateSignStore((state) => state.actions);

  const { data: signSupport } = useSignSupportQuery();
  const { data: signGroup, error } = useSignGroupQuery();

  useEffect(() => {
    setOpenProcessModal(openProcess ?? false);
  }, [openProcess]);

  useSignSupportFlyTo();

  if (error)
    return (
      <Box>
        <Box sx={{ width: '100%', height: '40px', textAlign: 'right', pr: 2 }}>
          <IconButtonLink
            to="/verkehrszeichen/$adminLevelId"
            params={{ adminLevelId: adminLevelId || 'recherche' }}
            variant="plain"
            size="sm"
            sx={{ height: '40px' }}
          >
            <CrossIcon height="24px" width="24px" stroke="black" />
          </IconButtonLink>
        </Box>
        <ErrorAlert error={error} />
      </Box>
    );

  if (!signSupportId || !signGroupId || !signSupport || !signGroup) {
    return null;
  }

  return (
    <StyledStack>
      <Stack direction="row" justifyContent="space-between" sx={{ width: '100%', height: '40px' }}>
        <ButtonLink
          color="neutral"
          variant="plain"
          startDecorator={<ExpandArrowIcon style={{ transform: 'rotate(90deg)' }} width={24} height={24} />}
          to="/verkehrszeichen/$adminLevelId/$signSupportId"
          size="sm"
          params={{ adminLevelId: adminLevelId || 'recherche', signSupportId: signSupport.id }}
          sx={{ fontSize: '14px', color: globalTheme.palette.neutral.plainColor, height: '40px' }}
        >
          Aufstellvorrichtung
        </ButtonLink>
        <IconButtonLink
          to="/verkehrszeichen/$adminLevelId"
          params={{ adminLevelId: adminLevelId || 'recherche' }}
          variant="plain"
          size="sm"
        >
          <CrossIcon height="24px" width="24px" stroke="black" />
        </IconButtonLink>
      </Stack>
      <Sheet
        variant="outlined"
        sx={{
          height: 'calc(100% - 40px);',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: globalTheme.radius.sm,
          borderWidth: '1px',
        }}
      >
        <StyledScrollBox>
          <StyledGroupAccordionGroup>
            <Accordion>
              <StyledAccordionSummary indicator={<ExpandArrowIcon />}>
                <Box sx={{ padding: '8px 12px' }}>Gruppe</Box>
              </StyledAccordionSummary>
              <AccordionDetails>
                <SignGroupDetails signGroup={signGroup} />
              </AccordionDetails>
            </Accordion>
          </StyledGroupAccordionGroup>

          <StyledSignAccordionGroup>
            {signGroup.signs
              ?.sort((a, b) => a.order - b.order)
              .map((sign, index) => (
                <StyledSignAccordion key={sign.id}>
                  <StyledAccordionSummary indicator={<ExpandArrowIcon />}>
                    <Box sx={{ padding: '8px 0' }}>
                      <SignCard key={sign.id} sign={sign} subSign={index > 0} />
                    </Box>
                  </StyledAccordionSummary>
                  <AccordionDetails>
                    <SignDetails sign={sign} />
                  </AccordionDetails>
                </StyledSignAccordion>
              ))}
          </StyledSignAccordionGroup>

          {adminLevelId && (
            <ButtonLink
              fullWidth
              sx={{ justifyContent: 'flex-start' }}
              startDecorator={<PlusCircleIcon stroke={globalTheme.palette.primary.plainColor} />}
              variant="plain"
              to="/verkehrszeichen/$adminLevelId/$signSupportId/$signGroupId/hinzufuegen"
              params={{ adminLevelId, signSupportId, signGroupId }}
              onClick={() => setStep(CreateSignStep.ADD_GROUP)}
            >
              Verkehrszeichen hinzufügen
            </ButtonLink>
          )}

          <Outlet />
        </StyledScrollBox>
        {adminLevelId && (
          <StyledActionButtonStack direction="row" useFlexGap sx={{ flexWrap: 'wrap' }}>
            <Button startDecorator={<EditIcon />} onClick={() => setOpenEditModal(true)} variant="outlined" size="sm">
              Bearbeiten
            </Button>
            <Button
              startDecorator={<DuplicateIcon />}
              onClick={() => setOpenDuplicateModal(true)}
              variant="outlined"
              size="sm"
            >
              Duplizieren
            </Button>
            <Button
              startDecorator={<ClockIcon />}
              onClick={() => setOpenProcessModal(true)}
              variant="outlined"
              size="sm"
            >
              Vorgänge
            </Button>
            {openEditModal && <EditSwitchModal setOpen={setOpenEditModal} />}
            {openDuplicateModal && <DuplicateModal setOpen={setOpenDuplicateModal} />}
            {openProcessModal && <ProcessModal setOpen={setOpenProcessModal} />}
          </StyledActionButtonStack>
        )}
      </Sheet>
    </StyledStack>
  );
}
